import { ButtonWithLoading } from "@dexteel/mesf-core";
import CachedIcon from "@mui/icons-material/Cached";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useRef, useState } from "react";

import {
	getFlowSheets,
	uploadFlowSheetFile,
	upsertFlowSheet,
} from "@/pages/configuration/repositories/FlowSheetRepository";
import {
	buildStandDrawingRows,
	formatBilletSize,
	mergeBilletSizes,
	type SectionFlowSheets,
} from "../models/FlowSheet";
import { BilletSizeSelector } from "./BilletSizeSelector";

const useStandDrawings = ({
	sectionId,
	isDiagonal,
	billetSize,
}: {
	sectionId: number;
	isDiagonal: boolean;
	billetSize: number | null;
}) =>
	useQuery<SectionFlowSheets, Error>({
		queryKey: ["flowSheets", sectionId, isDiagonal, billetSize],
		queryFn: ({ signal }) =>
			getFlowSheets({ sectionId, isDiagonal, billetSize }, signal),
		keepPreviousData: true,
	});

interface SectionFlowSheetProps {
	sectionId: number;
	sectionBilletSize: number | null;
	onError: (error: Error) => void;
}

interface UploadTarget {
	standId: number;
	standName: string;
}

export const SectionFlowSheet = ({
	sectionId,
	sectionBilletSize,
	onError,
}: SectionFlowSheetProps) => {
	const queryClient = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDiagonal, setIsDiagonal] = useState(false);
	const [diagonalBilletSize, setDiagonalBilletSize] = useState<number | null>(
		null,
	);
	const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);

	const billetSize = isDiagonal ? diagonalBilletSize : null;

	const { data, isLoading, error, refetch } = useStandDrawings({
		sectionId,
		isDiagonal,
		billetSize,
	});

	const standRows = buildStandDrawingRows(data?.drawings ?? []);
	const billetSizes = mergeBilletSizes(data?.billetSizes ?? [], [
		sectionBilletSize,
	]);

	const uploadMutation = useMutation({
		mutationFn: async ({ file, standId }: UploadTarget & { file: File }) => {
			const upload = await uploadFlowSheetFile(file);
			await upsertFlowSheet({
				sectionId,
				standId,
				isDiagonal,
				billetSize,
				uploadFileId: upload.uploadFileId,
			});
			return upload;
		},
		onSuccess: async (_, target) => {
			await queryClient.invalidateQueries({
				queryKey: ["flowSheets", sectionId],
			});
			enqueueSnackbar(
				billetSize === null
					? `Drawing uploaded for ${target.standName}`
					: `Drawing uploaded for ${target.standName} (${formatBilletSize(billetSize)})`,
				{ variant: "success" },
			);
		},
		onError,
	});
	const uploadingStandId = uploadMutation.isPending
		? (uploadMutation.variables?.standId ?? null)
		: null;

	const requestUpload = (target: UploadTarget) => {
		setUploadTarget(target);
		fileInputRef.current?.click();
	};

	const handleFileSelected = (file: File | null) => {
		const target = uploadTarget;
		setUploadTarget(null);
		if (file === null || target === null) return;

		if (file.type !== "application/pdf") {
			onError(new Error("The flow sheet drawing must be a PDF file"));
			return;
		}
		uploadMutation.mutate({ ...target, file });
	};

	const retryLoading = () => {
		void refetch();
	};

	return (
		<Stack spacing={1.25}>
			<input
				ref={fileInputRef}
				data-test-id="flow-sheet-file-input"
				type="file"
				accept="application/pdf"
				hidden
				onChange={(event) => {
					handleFileSelected(event.target.files?.[0] ?? null);
					event.target.value = "";
				}}
			/>

			<Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
				<Typography variant="body2" color="text.secondary">
					Reference drawings, one per stand.
				</Typography>

				<ToggleButtonGroup
					exclusive
					size="small"
					value={isDiagonal}
					data-test-id="flow-sheet-variant-toggle"
					onChange={(_event, value: boolean | null) => {
						if (value !== null) setIsDiagonal(value);
					}}
					sx={{
						"& .MuiToggleButton-root": {
							px: 1.5,
							py: 0.25,
							textTransform: "none",
						},
					}}
				>
					<ToggleButton value={false} data-test-id="flow-sheet-straight-button">
						Horizontal
					</ToggleButton>
					<ToggleButton value={true} data-test-id="flow-sheet-diagonal-button">
						Diagonal
					</ToggleButton>
				</ToggleButtonGroup>

				<BilletSizeSelector
					billetSizes={billetSizes}
					value={diagonalBilletSize}
					onChange={setDiagonalBilletSize}
					hidden={!isDiagonal}
				/>
			</Stack>

			{error ? (
				<Alert
					severity="error"
					data-test-id="flow-sheet-error"
					action={
						<Button
							color="inherit"
							size="small"
							data-test-id="flow-sheet-retry-button"
							onClick={retryLoading}
						>
							Retry
						</Button>
					}
				>
					{error.message}
				</Alert>
			) : isLoading ? (
				<Box
					data-test-id="flow-sheet-loading"
					sx={{ display: "flex", justifyContent: "center", py: 4 }}
				>
					<CircularProgress size={28} />
				</Box>
			) : standRows.length === 0 ? (
				<Alert severity="info" data-test-id="flow-sheet-no-stands">
					No stands are configured for the mill.
				</Alert>
			) : (
				<Table size="small" aria-label="Flow sheet drawings by stand">
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 90 }}>Stand</TableCell>
							<TableCell>Drawing</TableCell>
							<TableCell align="right" sx={{ width: 230 }} />
						</TableRow>
					</TableHead>
					<TableBody>
						{standRows.map((row) => (
							<TableRow
								key={row.StandId}
								hover
								data-test-id="flow-sheet-stand-row"
							>
								<TableCell>
									<Typography
										variant="body2"
										color="primary"
										fontWeight={600}
										data-test-id="flow-sheet-stand-name"
									>
										{row.StandName}
									</Typography>
								</TableCell>
								<TableCell>
									{row.Url === null ? (
										<Typography
											variant="body2"
											color="text.disabled"
											data-test-id="flow-sheet-no-drawing"
										>
											No drawing available
										</Typography>
									) : (
										<Stack direction="row" alignItems="center" gap={0.75}>
											<PictureAsPdfIcon
												sx={{ color: "error.main", fontSize: 18 }}
											/>
											<Typography
												variant="body2"
												data-test-id="flow-sheet-file-name"
											>
												{row.FileName}
											</Typography>
										</Stack>
									)}
								</TableCell>
								<TableCell align="right">
									<Stack direction="row" gap={1} justifyContent="flex-end">
										<ButtonWithLoading
											variant="text"
											size="small"
											startIcon={
												row.Url === null ? (
													<UploadFileIcon sx={{ fontSize: 18 }} />
												) : (
													<CachedIcon sx={{ fontSize: 18 }} />
												)
											}
											data-test-id="flow-sheet-upload-button"
											isLoading={uploadingStandId === row.StandId}
											disabled={uploadMutation.isPending}
											onClick={() =>
												requestUpload({
													standId: row.StandId,
													standName: row.StandName,
												})
											}
											sx={{
												fontSize: "0.8125rem",
												fontWeight: 500,
												textTransform: "none",
												whiteSpace: "nowrap",
												color:
													row.Url === null ? "primary.main" : "text.secondary",
												"&:hover": { backgroundColor: "action.hover" },
											}}
										>
											{row.Url === null ? "Upload" : "Replace"}
										</ButtonWithLoading>
										<Button
											component="a"
											href={row.Url ?? undefined}
											target="_blank"
											rel="noopener noreferrer"
											disabled={row.Url === null}
											data-test-id="flow-sheet-view-button"
											variant="outlined"
											color="primary"
											size="small"
											startIcon={
												<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
											}
											sx={{
												fontSize: "0.8125rem",
												fontWeight: 500,
												textTransform: "none",
												whiteSpace: "nowrap",
												backgroundColor: "background.paper",
												borderColor: "divider",
												"&:hover": {
													backgroundColor: "action.hover",
													borderColor: "primary.main",
												},
											}}
										>
											View
										</Button>
									</Stack>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</Stack>
	);
};
